import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns true if the line is in a transient state Sophia is currently
 * finalizing (status *InProgress, packageOption.inProgress, international
 * option inProgress). Used to drive frontend polling so the user sees the
 * finalized state without manual refresh.
 */
export function isLineInProgress(line: {
  status?: string | null;
  packageOptions?: Array<{ inProgress?: boolean }> | null;
  internationalOptionState?: { inProgress?: boolean } | null;
}): boolean {
  if (!line) return false;
  const status = line.status ?? '';
  if (/inprogress/i.test(status) || status === 'in_progress') return true;
  if (line.packageOptions?.some((o) => o?.inProgress)) return true;
  if (line.internationalOptionState?.inProgress) return true;
  return false;
}

/**
 * Calculates Easter Sunday date for a given year using the algorithm of Gauss
 * @param year - The year to calculate Easter for
 * @returns The date of Easter Sunday
 */
function getEasterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * Gets all French public holidays for a given year
 * @param year - The year to get holidays for
 * @returns Array of dates representing French public holidays
 */
function getFrenchHolidays(year: number): Date[] {
  const holidays: Date[] = [];

  // Fixed holidays
  holidays.push(new Date(year, 0, 1)); // Jour de l'an
  holidays.push(new Date(year, 4, 1)); // Fête du travail
  holidays.push(new Date(year, 4, 8)); // Victoire en Europe
  holidays.push(new Date(year, 6, 14)); // Fête nationale
  holidays.push(new Date(year, 7, 15)); // Assomption
  holidays.push(new Date(year, 10, 1)); // Toussaint
  holidays.push(new Date(year, 10, 11)); // Armistice
  holidays.push(new Date(year, 11, 25)); // Noël

  // Variable holidays (based on Easter)
  const easter = getEasterDate(year);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  holidays.push(easterMonday);

  const ascension = new Date(easter);
  ascension.setDate(easter.getDate() + 39);
  holidays.push(ascension);

  const whitMonday = new Date(easter);
  whitMonday.setDate(easter.getDate() + 50);
  holidays.push(whitMonday);

  return holidays;
}

/**
 * Checks if a date is a weekend (Saturday or Sunday)
 * @param date - The date to check
 * @returns True if the date is a weekend
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
}

/**
 * Checks if a date is a French public holiday
 * @param date - The date to check
 * @returns True if the date is a French public holiday
 */
export function isFrenchHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const holidays = getFrenchHolidays(year);
  
  return holidays.some((holiday) => {
    return (
      holiday.getDate() === date.getDate() &&
      holiday.getMonth() === date.getMonth() &&
      holiday.getFullYear() === date.getFullYear()
    );
  });
}

/**
 * Checks if a date is a weekend or a French public holiday
 * @param date - The date to check
 * @returns True if the date is a weekend or holiday
 */
export function isWeekendOrHoliday(date: Date): boolean {
  return isWeekend(date) || isFrenchHoliday(date);
}
